import {
    useLayoutEffect,
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    RefreshControl,
    TouchableOpacity,
    FlatList,
    Linking,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import NotificationCard from "./components/notificationCard";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    NotificationItemType
} from "../../../services/restAPI/actions/getMyNotifications/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    CheckCheck,
    ChevronLeft,
    BellOff
} from "lucide-react-native";
import {
    RESTService
} from "../../../services/restAPI";
import {
    storage
} from "../../../storage";

const DELETED_NOTIFICATIONS_KEY = "deletedNotificationIDs";

const getDeletedNotificationIDs = (): Set<string> => {
    try {
        const stored = storage.getString(DELETED_NOTIFICATIONS_KEY);
        if(stored) {
            const parsed = JSON.parse(stored);
            if(Array.isArray(parsed)) {
                return new Set(parsed);
            }
        }
    } catch {
        return new Set();
    }
    return new Set();
};

const Notifications = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [
        notifications,
        setNotifications
    ] = useState<NotificationItemType[]>([]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        hasMore,
        setHasMore
    ] = useState<boolean>(true);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const handleDeleteNotification = useCallback((notificationID: string) => {
        setNotifications((prev) => prev.filter((item) => item._id !== notificationID));

        RESTService.action("DeleteNotification", {
            notificationID
        }).catch(() => {
        });

        try {
            const currentDeleted = getDeletedNotificationIDs();
            currentDeleted.add(notificationID);
            storage.set(DELETED_NOTIFICATIONS_KEY, JSON.stringify(Array.from(currentDeleted)));
        } catch {
            return;
        }
    }, []);

    const fetchNotifications = useCallback((pageNumber: number, isRefresh: boolean = false) => {
        RESTService.action("GetMyNotifications", {
            page: pageNumber
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const fetched = res.payload?.notifications ?? [];
                    const currentPage = res.payload?.page ?? pageNumber;
                    const totalPages = res.payload?.pageCount ?? 1;
                    const deletedIDs = getDeletedNotificationIDs();
                    const filteredFetched = fetched.filter((item) => !deletedIDs.has(item._id));

                    setPage(currentPage);
                    setHasMore(currentPage < totalPages);

                    if(isRefresh) {
                        setNotifications(filteredFetched);
                    } else {
                        setNotifications((prev) => {
                            const existingIds = new Set(prev.map((item) => item._id));
                            const uniqueNewItems = filteredFetched.filter((item) => !existingIds.has(item._id));
                            return [
                                ...prev,
                                ...uniqueNewItems
                            ];
                        });
                    }
                }
            })
            .catch(() => {
            })
            .finally(() => {
                setIsLoading(false);
                setIsRefreshing(false);
            });
    }, []);

    useEffect(() => {
        fetchNotifications(1);
    }, [
        fetchNotifications
    ]);

    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        fetchNotifications(1, true);
    }, [
        fetchNotifications
    ]);

    const handleEndReached = useCallback(() => {
        if(!hasMore) {
            return;
        }
        if(isLoading) {
            return;
        }
        if(isRefreshing) {
            return;
        }
        fetchNotifications(page + 1);
    }, [
        fetchNotifications,
        isRefreshing,
        isLoading,
        hasMore,
        page
    ]);

    const handleMarkAllAsRead = useCallback(() => {
        const hasUnread = notifications.some((item) => !item.isRead);
        if(!hasUnread) {
            return;
        }

        setNotifications((prev) => prev.map((item) => ({
            ...item,
            isRead: true
        })));

        RESTService.action("MarkAllAsRead", null).catch(() => {
        });
    }, [
        notifications
    ]);

    const handleNotificationPress = useCallback((item: NotificationItemType) => {
        if(!item.isRead) {
            setNotifications((prev) => prev.map((n) => (n._id === item._id
                ? {
                    ...n,
                    isRead: true
                }
                : n)));

            RESTService.action("MarkAsRead", {
                notificationID: item._id
            }).catch(() => {
            });
        }

        const data = item.data;
        if(!data) {
            return;
        }

        if(typeof data.announcementID === "string") {
            navigation.navigate("AnnouncementDetail", {
                announcementID: data.announcementID
            });
            return;
        }

        if(typeof data.newsID === "string") {
            navigation.navigate("NewsDetail", {
                newsID: data.newsID
            });
            return;
        }

        if(typeof data.eventID === "string") {
            navigation.navigate("EventDetail", {
                eventTitle: item.title,
                eventID: data.eventID
            });
            return;
        }

        if(typeof data.formID === "string") {
            navigation.navigate("ApplyForm", {
                formID: data.formID
            });
            return;
        }

        if(typeof data.url === "string" && data.url.length > 0) {
            Linking.openURL(data.url).catch(() => {
            });
        }
    }, [
        navigation
    ]);

    const hasUnread = notifications.some((item) => !item.isRead);

    const renderHeaderRight = () => {
        if(!hasUnread) {
            return <View
                style={stylesheet.headerPlaceholder}
            />;
        }

        return <TouchableOpacity
            style={[
                stylesheet.markAllButton,
                {
                    padding: spaces.spacingSm
                }
            ]}
            onPress={handleMarkAllAsRead}
        >
            <CheckCheck
                color={colors.system.default.info}
                size={22}
            />
        </TouchableOpacity>;
    };

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerWrapper,
                    {
                        backgroundColor: colors.content.container.default,
                        paddingHorizontal: spaces.spacingMd,
                        paddingBottom: spaces.spacingSm,
                        paddingTop: inset.top + spaces.spacingSm
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        color={colors.content.icon.default}
                        size={28}
                    />
                </TouchableOpacity>
                <View
                    pointerEvents="none"
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("notifications")}
                    </Text>
                </View>
                {renderHeaderRight()}
            </View>
        });
    }, [
        handleMarkAllAsRead,
        hasUnread,
        navigation,
        localize,
        colors,
        spaces,
        inset
    ]);

    const renderEmpty = () => {
        if(isLoading) {
            return null;
        }

        return <View
            style={stylesheet.emptyContainer}
        >
            <View
                style={[
                    stylesheet.emptyIconContainer,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <BellOff
                    color={colors.content.icon.low}
                    size={48}
                />
            </View>
            <Text
                variant="bodyLargeSize"
                color="mid"
                style={stylesheet.emptyText}
            >
                {localize("noNotificationsYet")}
            </Text>
        </View>;
    };

    const renderFooter = () => {
        if(!hasMore || !isLoading) {
            return null;
        }

        return <View
            style={[
                stylesheet.footerLoader,
                {
                    paddingVertical: spaces.spacingMd
                }
            ]}
        >
            <ActivityIndicator
                color={colors.content.icon.default}
                size="small"
            />
        </View>;
    };

    if(isLoading && notifications.length === 0) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                color={colors.content.icon.default}
                size={40}
            />
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={false}
        style={stylesheet.container}
    >
        <FlatList
            data={notifications}
            renderItem={({
                item
            }) => <NotificationCard
                onPress={() => handleNotificationPress(item)}
                onDelete={handleDeleteNotification}
                item={item}
            />}
            keyExtractor={(item) => item._id}
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.5}
            ListEmptyComponent={renderEmpty}
            ListFooterComponent={renderFooter}
            refreshControl={<RefreshControl
                refreshing={isRefreshing}
                onRefresh={handleRefresh}
                colors={[
                    colors.system.default.info
                ]}
                tintColor={colors.system.default.info}
            />}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[
                stylesheet.listContent,
                {
                    paddingHorizontal: spaces.spacingMd,
                    paddingTop: spaces.spacingMd,
                    paddingBottom: spaces.spacingXl
                }
            ]}
            style={stylesheet.listContainer}
        />
    </PageContainer>;
};
export default Notifications;
